import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Home, AlertTriangle, RefreshCw, Search } from 'lucide-react';
import AnimatedBackground from './AnimatedBackground';
import { useTheme } from '../contexts/ThemeContext';

const NotFound = () => {
  const { theme } = useTheme();
  const [countdown, setCountdown] = useState(10);
  const [showCountdown, setShowCountdown] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get the previous route from session storage
    const previousRoute = sessionStorage.getItem('previousRoute') || sessionStorage.getItem('lastValidRoute') || '/';
    
    // Auto-redirect countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Redirect to previous route or home
          navigate(previousRoute, { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleGoBack = () => {
    const previousRoute = sessionStorage.getItem('previousRoute') || sessionStorage.getItem('lastValidRoute') || '/';
    navigate(previousRoute, { replace: true });
  };

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  const handleStopCountdown = () => {
    setShowCountdown(false);
    setCountdown(0);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Suggest similar routes based on the current wrong route
  const getSuggestedRoutes = () => {
    const currentPath = location.pathname.toLowerCase();
    const suggestions = [];

    if (currentPath.includes('school') || currentPath.includes('edu') || currentPath.includes('student') || currentPath.includes('erp')) {
      suggestions.push({ path: '/apnaeduhub', label: '📚 School ERP Software (ApnaEduHub)', desc: 'Complete school management solution' });
    }
    if (currentPath.includes('hotel') || currentPath.includes('hospitality') || currentPath.includes('booking') || currentPath.includes('restaurant') || currentPath.includes('pos')) {
      suggestions.push({ path: '/hotel-management', label: '🏨 Hotel Management System', desc: 'Hotel booking & restaurant POS' });
    }
    if (currentPath.includes('shop') || currentPath.includes('store') || currentPath.includes('commerce') || currentPath.includes('ecommerce')) {
      suggestions.push({ path: '/ecommerce', label: '🛒 E-commerce Platform', desc: 'Online store builder solution' });
    }
    if (currentPath.includes('demo') || currentPath.includes('trial') || currentPath.includes('free')) {
      suggestions.push({ path: '/apnaeduhub', label: '🎯 Free Demo Available', desc: 'Try our school ERP system' });
    }
    if (currentPath.includes('college') || currentPath.includes('university')) {
      suggestions.push({ path: '/apnaeduhub', label: '🎓 College Management System', desc: 'Higher education ERP solution' });
    }

    // If no specific suggestions, add popular ones
    if (suggestions.length === 0) {
      suggestions.push(
        { path: '/apnaeduhub', label: '📚 School ERP System (ApnaEduHub)', desc: 'India\'s best school management software' },
        { path: '/hotel-management', label: '🏨 Hotel Management Software', desc: 'Complete hotel & restaurant solution' },
        { path: '/ecommerce', label: '🛒 E-commerce Platform', desc: 'Build your online store today' }
      );
    }

    return suggestions.slice(0, 4); // Limit to 4 suggestions
  };

  return (
    <div className={`relative min-h-screen overflow-hidden ${
      theme === 'light' ? 'bg-gray-50' : 'bg-gray-900'
    }`}>
      <AnimatedBackground />
      
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-2xl text-center">
          {/* 404 Animation */}
          <div className="mb-8">
            <div className="relative">
              <div className="mb-4 font-bold text-transparent text-8xl md:text-9xl bg-gradient-to-r from-red-400 via-purple-500 to-blue-600 bg-clip-text animate-pulse">
                404
              </div>
              <div className="absolute inset-0 font-bold text-8xl md:text-9xl text-red-500/10 animate-ping">
                404
              </div>
            </div>
            <AlertTriangle className="w-16 h-16 mx-auto text-yellow-400 animate-bounce" />
          </div>

          {/* Error Message */}
          <div className="mb-8">
            <h1 className={`mb-4 text-3xl font-bold md:text-4xl ${
              theme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>
              🚫 Oops! Page Not Found
            </h1>
            <p className={`mb-4 text-lg ${
              theme === 'light' ? 'text-gray-700' : 'text-gray-300'
            }`}>
              The page you're looking for doesn't exist. But don't worry! <strong>NeuroSpark Technologies</strong> has amazing solutions waiting for you.
            </p>
            <div className={`p-4 mb-4 border bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-blue-500/30 rounded-xl backdrop-blur-sm ${
              theme === 'light' ? 'bg-white/80' : ''
            }`}>
              <div className="text-left">
                <div className="mb-2 text-sm font-semibold text-green-400">✅ Available Solutions:</div>
                <div className={`space-y-1 text-sm ${
                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                }`}>
                  <div>📚 School ERP Software (ApnaEduHub)</div>
                  <div>🎓 College Management System</div>
                  <div>🏨 Hotel Management Software</div>
                  <div>🛒 E-commerce Platform Solutions</div>
                  <div>🎯 Free Demo & 24/7 Support Available</div>
                </div>
              </div>
            </div>
            <div className={`p-3 border rounded-lg backdrop-blur-sm ${
              theme === 'light'
                ? 'border-gray-300 bg-white/50'
                : 'border-gray-700 bg-gray-800/50'
            }`}>
              <p className={`mb-1 text-sm ${
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              }`}>Requested URL:</p>
              <p className="font-mono text-sm text-red-400 break-all">{location.pathname}</p>
            </div>
          </div>

          {/* Auto Redirect Info */}
          {showCountdown && countdown > 0 && (
            <div className={`p-6 mb-8 border bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-blue-500/30 rounded-xl backdrop-blur-sm ${
              theme === 'light' ? 'bg-white/80' : ''
            }`}>
              <p className="mb-2 text-blue-300">
                🔄 Auto-redirecting to previous page in
              </p>
              <div className="mb-3 text-3xl font-bold text-blue-400">
                {countdown} seconds
              </div>
              <button
                onClick={handleStopCountdown}
                className={`text-sm transition-colors ${
                  theme === 'light'
                    ? 'text-gray-600 hover:text-gray-900'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Cancel auto-redirect
              </button>
            </div>
          )}

          {/* Suggested Routes */}
          <div className="mb-8">
            <h3 className={`mb-4 text-xl font-semibold ${
              theme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>
              🎯 Popular NeuroSpark Solutions - Did you mean?
            </h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {getSuggestedRoutes().map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => navigate(suggestion.path)}
                  className={`p-4 text-left transition-all duration-300 border rounded-lg group ${
                    theme === 'light'
                      ? 'border-gray-300 bg-white/50 hover:bg-blue-50 hover:border-blue-400'
                      : 'border-gray-700 bg-gradient-to-r from-gray-800/50 to-gray-700/50 hover:from-blue-600/20 hover:to-purple-600/20 hover:border-blue-500/50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Search className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      theme === 'light'
                        ? 'text-gray-600 group-hover:text-blue-600'
                        : 'text-gray-400 group-hover:text-blue-400'
                    }`} />
                    <div>
                      <div className={`mb-1 font-medium ${
                        theme === 'light'
                          ? 'text-gray-900 group-hover:text-blue-700'
                          : 'text-white group-hover:text-blue-300'
                      }`}>
                        {suggestion.label}
                      </div>
                      <div className={`mb-1 text-xs ${
                        theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                      }`}>{suggestion.desc}</div>
                      <div className={`text-xs ${
                        theme === 'light' ? 'text-gray-500' : 'text-gray-500'
                      }`}>{suggestion.path}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
            <button
              onClick={handleGoBack}
              className="flex items-center justify-center gap-2 px-6 py-3 font-semibold text-white transition-all duration-300 transform bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-xl hover:scale-105"
            >
              <ArrowLeft className="w-5 h-5" />
              Go Back
            </button>
            
            <button
              onClick={handleGoHome}
              className="flex items-center justify-center gap-2 px-6 py-3 font-semibold text-white transition-all duration-300 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 rounded-xl"
            >
              <Home className="w-5 h-5" />
              Homepage
            </button>

            <button
              onClick={handleRefresh}
              className="flex items-center justify-center gap-2 px-6 py-3 font-semibold text-white transition-all duration-300 bg-gray-700 hover:bg-gray-600 rounded-xl"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </button>
          </div>

          {/* Additional Help */}
          <div className={`text-sm ${
            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
          }`}>
            <p className="mb-2">
              🤔 Still having trouble? Here are some helpful tips:
            </p>
            <div className={`p-4 text-left border rounded-lg ${
              theme === 'light'
                ? 'border-gray-300 bg-white/30'
                : 'border-gray-700 bg-gray-800/30'
            }`}>
              <ul className="space-y-1 text-xs">
                <li>• Check if the URL is spelled correctly</li>
                <li>• The page might have been moved or deleted</li>
                <li>• Try using the navigation menu to find what you're looking for</li>
                <li>• Contact support if you believe this is an error</li>
              </ul>
            </div>
          </div>

          {/* NeuroSpark Branding */}
          <div className="mt-8 text-center">
            <p className={`text-sm ${
              theme === 'light' ? 'text-gray-500' : 'text-gray-500'
            }`}>
              Powered by <span className="font-semibold text-transparent bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text">NeuroSpark Technologies</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
